import {
  Breadcrumb,
  getTheme,
  IBreadcrumbItem,
  Icon,
  mergeStyleSets,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { Question } from '../../Question';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';

const PRODUCT_URL = 'https://reenhanced.com/product/gravity-forms-power-automate-pro/';
const LOGO_URL = 'https://reenhanced.com/wp-content/uploads/2024/03/gf-pro-with-background.jpg';
const SCREENSHOT_TRIGGER = 'https://reenhanced.com/wp-content/uploads/2024/03/trigger_note.png';
const SCREENSHOT_ENTRIES = 'https://reenhanced.com/wp-content/uploads/2024/03/get_entrires.png';
const SCREENSHOT_CREATE = 'https://reenhanced.com/wp-content/uploads/2024/03/create_entry.png';

const theme = getTheme();

const style = mergeStyleSets({
  container: {},
  heroBanner: {
    background: `linear-gradient(135deg, ${theme.palette.themePrimary} 0%, ${theme.palette.themeDarker} 100%)`,
    borderRadius: 12,
    padding: '32px 36px',
    marginBottom: 28,
    color: '#fff',
  },
  heroLogoRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  heroLogo: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover' as const, flexShrink: 0 },
  heroTitle: { ...theme.fonts.xxLarge, color: '#fff', margin: 0, fontWeight: 700 },
  heroSubtitle: { ...theme.fonts.mediumPlus, color: 'rgba(255,255,255,0.88)', marginBottom: 20, lineHeight: '1.6' },
  heroPricing: { ...theme.fonts.small, color: 'rgba(255,255,255,0.7)', marginTop: 8, display: 'block' as const },
  trustRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginBottom: 28 },
  trustPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 20, padding: '5px 12px', fontSize: 13,
    color: theme.palette.neutralPrimary,
  },
  sectionTitle: { ...theme.fonts.xLarge, fontWeight: 700, marginBottom: 16, marginTop: 24 },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 24 },
  featureCard: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8, padding: '14px 16px',
  },
  featureIcon: { color: theme.palette.themePrimary, fontSize: 20, marginBottom: 6, display: 'block' as const },
  featureTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 4 },
  featureText: { ...theme.fonts.small, color: theme.palette.neutralSecondary, lineHeight: '1.5' },
  screenshot: {
    width: '100%', maxWidth: 560, borderRadius: 8,
    border: `1px solid ${theme.palette.neutralLight}`,
    display: 'block' as const, margin: '16px 0',
  },
  useCaseList: { listStyle: 'none', paddingLeft: 0, margin: 0 },
  useCaseItem: { display: 'flex', gap: 12, padding: '12px 0', borderBottom: `1px solid ${theme.palette.neutralLighter}` },
  useCaseIcon: { color: theme.palette.themePrimary, fontSize: 20, flexShrink: 0, marginTop: 2 },
  useCaseTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 2 },
  useCaseDesc: { ...theme.fonts.small, color: theme.palette.neutralSecondary },
  securityBox: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 6, padding: '16px 20px', marginBottom: 16,
  },
  highlight: { backgroundColor: 'rgba(255, 245, 100, 0.65)', padding: '0 4px', borderRadius: 2 },
});

const useCases = [
  { icon: 'ContactCard', title: 'Customer portal with per-user data', desc: 'Build a WordPress-powered portal where each visitor sees only their own data, connected live to Dataverse or SharePoint via GravityView.' },
  { icon: 'DocumentApproval', title: 'Document signing & PDF generation', desc: 'Combine Gravity PDF with a Signature field to auto-generate signed PDFs and save them to a SharePoint folder or Azure Blob Storage.' },
  { icon: 'CheckList', title: 'Organisation-wide approval workflow', desc: 'Route form submissions through Microsoft Approvals so the right people review and authorise before data flows downstream.' },
  { icon: 'ExcelLogo', title: 'Live Excel reporting from form entries', desc: 'Every new or updated form entry is appended to a shared Excel workbook in real time — no exports needed.' },
  { icon: 'Money', title: 'End-to-end payment fulfilment', desc: 'Connect Gravity Forms payment add-ons to Business Central, F&O, or NAV to generate invoices and trigger order workflows automatically.' },
  { icon: 'Onboarding', title: 'Employee onboarding with automatic PDF filing', desc: 'New employee forms auto-generate onboarding PDFs and file them into the correct SharePoint folder — zero manual steps.' },
  { icon: 'TeamsLogo', title: 'Teams notifications on new submissions', desc: 'Announce new form entries in a Microsoft Teams channel the instant they are submitted.' },
  { icon: 'Database', title: 'Full CRM sync with Dynamics 365', desc: 'Write form entries directly to Dynamics 365 records, start customer journeys, and read CRM data back into the form.' },
];

export const GravityFormsPro: React.FC = () => {
  const breadcrumbs: IBreadcrumbItem[] = [
    { text: 'Products', key: 'products', onClick: (ev) => { ev.preventDefault(); navigateTo('/products'); } },
    { text: 'Gravity Forms Power Automate Professional', key: 'gfpro' },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbs} />
      <div className={style.container}>

        {/* ── Hero ── */}
        <div className={style.heroBanner}>
          <div className={style.heroLogoRow}>
            <img src={LOGO_URL} alt="Gravity Forms Pro" className={style.heroLogo} />
            <h1 className={style.heroTitle}>Gravity Forms Power Automate Professional</h1>
          </div>
          <p className={style.heroSubtitle}>
            The <strong style={{ color: '#fff' }}>official, Microsoft Certified</strong> Power Automate connector
            for Gravity Forms — with full read/write entry access, unlimited forms, bidirectional sync, and
            enterprise-grade security. Build customer portals, document workflows, payment integrations, and
            CRM sync without writing a single line of code.
          </p>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 14 }}>
            <PrimaryButton
              text="Start Free 60-Day Trial"
              iconProps={{ iconName: 'ShoppingCart' }}
              onClick={() => { window.location.href = PRODUCT_URL; }}
              styles={{ root: { backgroundColor: '#fff', color: theme.palette.themePrimary, borderColor: '#fff' } }}
            />
            <MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
          </Stack>
          <span className={style.heroPricing}>
            From $39 / month &nbsp;&bull;&nbsp; 60-day free trial &nbsp;&bull;&nbsp; Rated 5/5 by customers
          </span>
        </div>

        {/* ── Trust signals ── */}
        <div className={style.trustRow}>
          {[
            { icon: 'Certificate', text: 'Microsoft Certified' },
            { icon: 'Lock', text: 'Direct to Power Automate — No Intermediary' },
            { icon: 'Code', text: 'No Code Required' },
            { icon: 'ReadingMode', text: 'Read, Write, Edit & Delete Entries' },
            { icon: 'Unlimited', text: 'Unlimited Forms & Users' },
            { icon: 'DocumentManagement', text: 'Gravity PDF & GravityView Support' },
          ].map(t => (
            <span key={t.icon} className={style.trustPill}>
              <Icon iconName={t.icon} style={{ color: theme.palette.themePrimary }} />
              {t.text}
            </span>
          ))}
        </div>

        <Pivot>
          {/* ── Overview ── */}
          <PivotItem headerText="Overview">
            <Text block style={{ ...theme.fonts.mediumPlus as React.CSSProperties, lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
              This is the <span className={style.highlight}>Professional version</span> — a ground-up re-write
              focused on security, bidirectional data flows, and extensibility. Unlike the standard connector
              (which only has a single trigger), the Professional version has multiple triggers and actions that
              let you <strong>read, edit, delete, and create</strong> form entries directly from Power Automate.
              That opens up customer portals, two-way CRM sync, approval workflows, and full Microsoft ecosystem
              integration.
            </Text>

            <img src={SCREENSHOT_ENTRIES} alt="Get entries action in Power Automate" className={style.screenshot} />

            <div className={style.sectionTitle}>Why Professional?</div>
            <div className={style.featureGrid}>
              {[
                { icon: 'Sync', title: 'Bidirectional Data Flows', text: 'Read, edit, delete, and create form entries from any Power Automate flow — not just react to submissions.' },
                { icon: 'Lock', title: 'No Intermediary Server', text: 'Form submissions go directly from WordPress to Power Automate. A leaked license key cannot compromise your site.' },
                { icon: 'Lightning', title: 'Re-Trigger Historical Entries', text: 'Added a new flow? Use Re-Trigger to send all existing entries through it instantly — no data loss.' },
                { icon: 'Mail', title: 'External Error Reporting', text: 'Never miss a failed trigger. Provide an email and you\'ll be notified the moment anything goes wrong.' },
                { icon: 'Dropdown', title: 'Dynamic Field Sync', text: 'Update dropdown choices in any form field from any external data source via a Power Automate action.' },
                { icon: 'DocumentManagement', title: 'Gravity PDF + GravityView', text: 'Auto-generate PDFs and save to SharePoint. Build portals where each user sees only their own data.' },
                { icon: 'Money', title: 'Payment Integration', text: 'Connect payment add-ons to Business Central, NAV, or F&O. Create invoices and trigger fulfilment flows.' },
                { icon: 'Trackers', title: 'Full Trigger Tracking', text: 'Notes are added on all trigger events so you can trace any issue back to the exact entry and timestamp.' },
              ].map(f => (
                <div key={f.icon} className={style.featureCard}>
                  <Icon iconName={f.icon} className={style.featureIcon} />
                  <div className={style.featureTitle}>{f.title}</div>
                  <div className={style.featureText}>{f.text}</div>
                </div>
              ))}
            </div>

            <img src={SCREENSHOT_TRIGGER} alt="Trigger note added on form submission" className={style.screenshot} />
            <img src={SCREENSHOT_CREATE} alt="Create entry action" className={style.screenshot} />
          </PivotItem>

          {/* ── Use Cases ── */}
          <PivotItem headerText="Use Cases">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>What You Can Build — Without Writing a Single Line of Code</div>
            <ul className={style.useCaseList}>
              {useCases.map((uc, i) => (
                <li key={i} className={style.useCaseItem}>
                  <Icon iconName={uc.icon} className={style.useCaseIcon} />
                  <div>
                    <div className={style.useCaseTitle}>{uc.title}</div>
                    <div className={style.useCaseDesc}>{uc.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </PivotItem>

          {/* ── Security ── */}
          <PivotItem headerText="Security">
            <div className={style.securityBox} style={{ marginTop: 16 }}>
              <Text variant="large" block style={{ fontWeight: 'bold', marginBottom: 8 }}>
                <Icon iconName="Lock" /> Direct to Power Automate — More Secure By Design
              </Text>
              <Text block>
                Unlike the standard connector, the Professional version does <strong>not</strong> pass trigger data
                through any intermediary server. Your form submissions travel directly from your WordPress site to
                Microsoft Power Automate.
              </Text>
              <ul style={{ marginTop: 12 }}>
                <li>Each website gets a unique randomly-generated secret key</li>
                <li>Leaking your license key has <strong>zero impact</strong> on website security</li>
                <li>No direct link between external systems and your WordPress database</li>
                <li>SSL encryption on all connections</li>
                <li>A compromised WordPress site exposes only what is already in your database — nothing more</li>
                <li>Full documentation at <a href="https://docs.gravityconnector.com/security" target="_blank" rel="noreferrer">docs.gravityconnector.com/security</a></li>
              </ul>
            </div>
          </PivotItem>

          {/* ── FAQ ── */}
          <PivotItem headerText="FAQ">
            <Question question="How is this different from the standard connector?">
              <p>The standard connector only has a single trigger. The Professional version has multiple triggers plus actions to read, edit, delete, and create entries — enabling portals, two-way sync, and full Microsoft ecosystem integration. The two versions are incompatible; upgrading requires rebuilding your flows.</p>
            </Question>
            <Question question="Can I try before I buy?">
              <p>Yes — every plan starts with a 60-day free trial. No credit card required to start. Use code <code>onemonthfree</code> on monthly plans for an additional free month.</p>
            </Question>
            <Question question="Do you offer a single-domain version?">
              <p>No. You're a professional — you're using a staging/development environment, right?</p>
            </Question>
            <Question question="What if I need more than 100 domains?">
              <p>Start a trial, then open a support ticket. We'll be happy to work with you on enterprise pricing.</p>
            </Question>
            <Question question="Can I extend the plugin?">
              <p>Yes — you receive full source code at download. We've added actions and filters throughout. If you need a specific hook, reach out and we'll accommodate it in an update.</p>
            </Question>
            <Question question="Can you extend my trial?">
              <p>Sure — open a support ticket. We're happy to help.</p>
            </Question>
          </PivotItem>
        </Pivot>

        {/* ── Bottom CTA ── */}
        <div style={{
          backgroundColor: theme.palette.neutralLighterAlt,
          border: `1px solid ${theme.palette.neutralLight}`,
          borderRadius: 10, padding: '24px 28px', marginTop: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: 16,
        }}>
          <div>
            <Text block style={{ ...theme.fonts.large as React.CSSProperties, fontWeight: 700, marginBottom: 4 }}>
              Ready to unlock bidirectional Gravity Forms automation?
            </Text>
            <Text block style={{ color: theme.palette.neutralSecondary }}>
              From $39 / month &nbsp;&bull;&nbsp; 60-day free trial &nbsp;&bull;&nbsp; Microsoft Certified
            </Text>
          </div>
          <PrimaryButton
            text="Start Free 60-Day Trial"
            iconProps={{ iconName: 'ShoppingCart' }}
            onClick={() => { window.location.href = PRODUCT_URL; }}
          />
        </div>

      </div>
    </>
  );
};

export default GravityFormsPro;
